// src/pages/ApplicationChampionshipMatches/components/MatchEditModal/index.tsx
import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  Grid,
  IconButton,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  Textarea,
  useToast,
  Box,
} from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Control,
  Controller,
  FieldPath,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';

import { SelectField } from '@/components/SelectField';
import { InputField } from '@/components/InputField';
import { ControlledSelect } from '@/components/ControlledSelect';
import ControlTeamSelectAutocomplete from '@/components/ControlTeamSelectAutocomplete';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { Club, HyperlinkTarget, MatchStatus, MatchV2, translateMatchStatus } from '@/model';
import { GetParamsObject } from '@/model/query-params';
import { PutMatchV2Request } from '@/services/request/match_v2';
import { usePutMatchV2Mutation } from '@/services/queries/match_v2';
import { useSeasonTeams } from '@/services/queries/season-team';

const I18N_PREFIX = 'pages.application_championship_matches.components.match_edit_modal';

export interface MatchEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: MatchV2;
  onDone: () => void;
}

type LastMatchField = {
  // Mantidos como string no form pra permitir o usuário apagar/digitar "0"
  // sem o componente tratá-lo como "vazio". Convertidos a number no submit.
  home_goals: string;
  away_goals: string;
  home_team_uuid: string;
  home_team_name: string;
  away_team_uuid: string;
  away_team_name: string;
};

type ModalFormData = {
  home_team_uuid: string;
  away_team_uuid: string;
  match_date: string;
  status: MatchStatus;
  home_goals: string;
  away_goals: string;
  stadium: string;
  transmission: string;
  hyperlink: string;
  hyperlink_target: HyperlinkTarget;
  round: string;
  description: string;
  extra_time_home: string;
  extra_time_away: string;
  penalties_home: string;
  penalties_away: string;
  is_show_information: boolean;
  is_last_matches: boolean;
  last_matches: LastMatchField[];
  is_history_matches: boolean;
  history_matches: {
    wins: string;
    draw: string;
    lose: string;
  };
};

const toDateTimeLocal = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

const toFormState = (item: MatchV2): ModalFormData => ({
  home_team_uuid: item.home_team_uuid ?? '',
  away_team_uuid: item.away_team_uuid ?? '',
  match_date: toDateTimeLocal(item.match_date),
  status: item.status,
  home_goals:
    item.home_goals !== undefined && item.home_goals !== null ? String(item.home_goals) : '',
  away_goals:
    item.away_goals !== undefined && item.away_goals !== null ? String(item.away_goals) : '',
  stadium: item.stadium ?? '',
  transmission: item.transmission ?? '',
  hyperlink: item.hyperlink ?? '',
  hyperlink_target: item.hyperlink_target ?? HyperlinkTarget.EXTERNAL,
  round: item.round !== undefined && item.round !== null ? String(item.round) : '',
  description: item.description ?? '',
  extra_time_home:
    item.extra_time_home !== undefined && item.extra_time_home !== null
      ? String(item.extra_time_home)
      : '',
  extra_time_away:
    item.extra_time_away !== undefined && item.extra_time_away !== null
      ? String(item.extra_time_away)
      : '',
  penalties_home:
    item.penalties_home !== undefined && item.penalties_home !== null
      ? String(item.penalties_home)
      : '',
  penalties_away:
    item.penalties_away !== undefined && item.penalties_away !== null
      ? String(item.penalties_away)
      : '',
  is_show_information: !!item.is_show_information,
  is_last_matches: !!item.is_last_matches,
  last_matches: (item.last_matches ?? []).map(lm => ({
    home_goals: String(lm.home_goals ?? 0),
    away_goals: String(lm.away_goals ?? 0),
    home_team_uuid: lm.home_team_uuid,
    home_team_name: lm.home_team_name,
    away_team_uuid: lm.away_team_uuid,
    away_team_name: lm.away_team_name,
  })),
  is_history_matches: !!item.is_history_matches,
  history_matches: {
    wins: String(item.history_matches?.wins ?? 0),
    draw: String(item.history_matches?.draw ?? 0),
    lose: String(item.history_matches?.lose ?? 0),
  },
});

const parseNumericOrNull = (value: string): number | null => {
  if (value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Input numérico que opera sobre uma string no form:
 * - Permite o usuário digitar/manter "0" sem que o componente trate como
 *   vazio (bug do "não consegue digitar 0").
 * - Alinha o conteúdo à direita.
 * - Sanitiza para apenas dígitos (sem negativos, sem decimais).
 * - Remove zeros à esquerda quando há mais de um dígito (evita "02").
 * - No submit, o valor final é convertido a number.
 */
const NumericFormInput = ({
  control,
  name,
  width,
  variant = 'outline',
}: {
  control: Control<ModalFormData>;
  name: FieldPath<ModalFormData>;
  width?: string;
  /**
   * 'outline' = caixinha branca com borda (usada em last_matches/history).
   * 'flushed' = underline padrão do InputField (usada no grid principal).
   */
  variant?: 'outline' | 'flushed';
}) => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => {
      const display = field.value == null ? '' : String(field.value);
      const visualProps =
        variant === 'flushed'
          ? ({ variant: 'flushed' as const } as const)
          : ({
              variant: 'outline' as const,
              bg: 'white',
              borderColor: 'gray.300',
              borderRadius: 8,
            } as const);
      return (
        <Input
          type='text'
          inputMode='numeric'
          textAlign='right'
          value={display}
          width={width}
          {...visualProps}
          onChange={event => {
            let digits = event.target.value.replace(/[^0-9]/g, '');
            if (digits.length > 1) digits = digits.replace(/^0+/, '') || '0';
            field.onChange(digits);
          }}
        />
      );
    }}
  />
);

export const MatchEditModal = ({ isOpen, onClose, item, onDone }: MatchEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const putMatch = usePutMatchV2Mutation();

  const { control, register, handleSubmit, setValue, reset } = useForm<ModalFormData>({
    defaultValues: toFormState(item),
    mode: 'onSubmit',
  });

  useEffect(() => {
    reset(toFormState(item));
  }, [item, reset]);

  // Times disponíveis (selects de Mandante/Visitante topo) = times da temporada.
  const seasonUuid = item.stage?.season?.uuid;
  const seasonTeamsParams = useMemo(
    () =>
      seasonUuid
        ? GetParamsObject(
            { season_uuid: seasonUuid } as Record<string, string>,
            1,
            200,
            'asc',
            'created_at',
          )
        : undefined,
    [seasonUuid],
  );
  const { data: seasonTeamsData } = useSeasonTeams(seasonTeamsParams, !!seasonUuid);
  // Seed Club[] deduplicado por uuid (season_teams pode ter múltiplas linhas
  // pro mesmo time — ex.: residual de fases/grupos diferentes).
  const seasonClubsSeed: Club[] = useMemo(() => {
    const map = new Map<string, Club>();
    (seasonTeamsData?.data ?? []).forEach(st => {
      const team = st.team;
      const uuid = st.team_uuid ?? team?.uuid;
      if (!team || !uuid || map.has(uuid)) return;
      map.set(uuid, {
        uuid,
        name: team.name,
        acronym: team.acronym,
        logo_path: team.logo_path ?? '',
        created_at: '',
        updated_at: '',
      });
    });
    return Array.from(map.values());
  }, [seasonTeamsData]);

  // Opções de last_matches: SOMENTE os 2 times do confronto atual. Quando o
  // user escolhe um, o outro select preenche o oponente automaticamente
  // (garante "sempre um contra o outro").
  const matchTeamsByUuid = useMemo(() => {
    const map = new Map<string, { uuid: string; name: string; logo?: string }>();
    if (item.home_team) {
      map.set(item.home_team_uuid, {
        uuid: item.home_team_uuid,
        name: item.home_team.name,
        logo: (item.home_team as { logo_path?: string }).logo_path,
      });
    }
    if (item.away_team) {
      map.set(item.away_team_uuid, {
        uuid: item.away_team_uuid,
        name: item.away_team.name,
        logo: (item.away_team as { logo_path?: string }).logo_path,
      });
    }
    return map;
  }, [item.home_team, item.home_team_uuid, item.away_team, item.away_team_uuid]);

  const matchTeamOptions = useMemo(
    () =>
      Array.from(matchTeamsByUuid.values()).map(team => ({
        value: team.uuid,
        label: team.name,
        imageUrl: team.logo ?? '',
      })),
    [matchTeamsByUuid],
  );

  const oppositeUuid = (uuid: string): string => {
    for (const teamUuid of matchTeamsByUuid.keys()) {
      if (teamUuid !== uuid) return teamUuid;
    }
    return '';
  };

  const { fields: lastMatchesFields, append: appendLastMatch, remove: removeLastMatch } =
    useFieldArray({ control, name: 'last_matches' });

  const isLastMatches = useWatch({ control, name: 'is_last_matches' });
  const isHistoryMatches = useWatch({ control, name: 'is_history_matches' });
  const hasTicketLink = !!useWatch({ control, name: 'hyperlink' })?.trim();

  const onSubmit = (data: ModalFormData) => {
    const stageUuid = item.stage?.uuid;
    if (!stageUuid) {
      toast({ status: 'error', title: t(`${I18N_PREFIX}.errors.missing_stage`) });
      return;
    }
    if (!data.home_team_uuid || !data.away_team_uuid) {
      toast({ status: 'error', title: t(`${I18N_PREFIX}.errors.missing_teams`) });
      return;
    }
    if (data.home_team_uuid === data.away_team_uuid) {
      toast({ status: 'error', title: t(`${I18N_PREFIX}.errors.same_team`) });
      return;
    }

    // Converte strings -> number nos goals (campos são string no form pra que
    // o input aceite "0" e edição vazia). Preenche team_name via cache.
    const normalizedLastMatches = (data.last_matches ?? [])
      .map(lm => ({
        home_goals: lm.home_goals === '' ? 0 : Number(lm.home_goals) || 0,
        away_goals: lm.away_goals === '' ? 0 : Number(lm.away_goals) || 0,
        home_team_uuid: lm.home_team_uuid,
        home_team_name:
          matchTeamsByUuid.get(lm.home_team_uuid)?.name ?? lm.home_team_name ?? '',
        away_team_uuid: lm.away_team_uuid,
        away_team_name:
          matchTeamsByUuid.get(lm.away_team_uuid)?.name ?? lm.away_team_name ?? '',
      }))
      .filter(lm => lm.home_team_uuid && lm.away_team_uuid);

    const payload: PutMatchV2Request = {
      stage_uuid: stageUuid,
      home_team_uuid: data.home_team_uuid,
      away_team_uuid: data.away_team_uuid,
      match_date: data.match_date ? new Date(data.match_date).toISOString() : null,
      stadium: data.stadium.trim() || null,
      transmission: data.transmission.trim() || null,
      hyperlink: data.hyperlink.trim() || null,
      hyperlink_target: data.hyperlink_target,
      description: data.description.trim() || null,
      round: parseNumericOrNull(data.round),
      home_goals: parseNumericOrNull(data.home_goals),
      away_goals: parseNumericOrNull(data.away_goals),
      extra_time_home: parseNumericOrNull(data.extra_time_home),
      extra_time_away: parseNumericOrNull(data.extra_time_away),
      penalties_home: parseNumericOrNull(data.penalties_home),
      penalties_away: parseNumericOrNull(data.penalties_away),
      status: data.status,
      is_show_information: data.is_show_information,
      is_last_matches: data.is_last_matches,
      last_matches: data.is_last_matches ? normalizedLastMatches : null,
      is_history_matches: data.is_history_matches,
      history_matches: data.is_history_matches
        ? {
            wins: data.history_matches.wins === '' ? 0 : Number(data.history_matches.wins) || 0,
            draw: data.history_matches.draw === '' ? 0 : Number(data.history_matches.draw) || 0,
            lose: data.history_matches.lose === '' ? 0 : Number(data.history_matches.lose) || 0,
          }
        : null,
    };

    putMatch.mutate(
      { id: item.uuid, payload },
      {
        onSuccess: () => {
          toast({ status: 'success', title: t(`${I18N_PREFIX}.toasts.update_success`) });
          onDone();
        },
        onError: (error: unknown) => {
          toast({
            status: 'error',
            title:
              (error as Error)?.message ||
              t(`${I18N_PREFIX}.toasts.update_error`),
          });
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='3xl' isCentered scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize='medium' fontWeight='bold'>
            {t(`${I18N_PREFIX}.title`)}
          </Text>
        </ModalHeader>
        <ModalBody>
          <Flex direction='column' gap={4}>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
              <ControlTeamSelectAutocomplete<ModalFormData, 'home_team_uuid'>
                control={control}
                name='home_team_uuid'
                clubs={seasonClubsSeed}
                width='100%'
                label={t(`${I18N_PREFIX}.field_home_team`)}
                placeholder={t(`${I18N_PREFIX}.placeholder_team`)}
                showFlag
                usePortal={false}
                prefetchOnOpen={false}
                minChars={9999}
              />

              <ControlTeamSelectAutocomplete<ModalFormData, 'away_team_uuid'>
                control={control}
                name='away_team_uuid'
                clubs={seasonClubsSeed}
                width='100%'
                label={t(`${I18N_PREFIX}.field_away_team`)}
                placeholder={t(`${I18N_PREFIX}.placeholder_team`)}
                showFlag
                usePortal={false}
                prefetchOnOpen={false}
                minChars={9999}
              />

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_date`)}</Text>
                <Controller
                  control={control}
                  name='match_date'
                  render={({ field }) => (
                    <InputField type='datetime-local' value={field.value} onChange={field.onChange} />
                  )}
                />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_status`)}</Text>
                <Controller
                  control={control}
                  name='status'
                  render={({ field }) => (
                    <SelectField value={field.value} onChange={field.onChange}>
                      {Object.values(MatchStatus).map(value => (
                        <option key={value} value={value}>
                          {translateMatchStatus(value)}
                        </option>
                      ))}
                    </SelectField>
                  )}
                />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_home_goals`)}</Text>
                <NumericFormInput control={control} name='home_goals' variant='flushed' />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_away_goals`)}</Text>
                <NumericFormInput control={control} name='away_goals' variant='flushed' />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_round`)}</Text>
                <NumericFormInput control={control} name='round' variant='flushed' />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_stadium`)}</Text>
                <InputField {...register('stadium')} />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_transmission`)}</Text>
                <InputField {...register('transmission')} />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_extra_time_home`)}</Text>
                <NumericFormInput control={control} name='extra_time_home' variant='flushed' />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_extra_time_away`)}</Text>
                <NumericFormInput control={control} name='extra_time_away' variant='flushed' />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_penalties_home`)}</Text>
                <NumericFormInput control={control} name='penalties_home' variant='flushed' />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_penalties_away`)}</Text>
                <NumericFormInput control={control} name='penalties_away' variant='flushed' />
              </InputGroup>
            </Grid>

            <Box borderWidth='1px' borderColor='gray.200' borderRadius={8} p={4}>
              <Flex direction='column' gap={4}>
                <Text fontWeight='bold'>{t(`${I18N_PREFIX}.tickets_section_title`)}</Text>

                <InputGroup display='flex' flexDirection='column'>
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_hyperlink`)}</Text>
                  <InputField {...register('hyperlink')} placeholder='https://' />
                </InputGroup>

                {/* Config dependente do link: sem link não há o que abrir. */}
                <InputGroup
                  display='flex'
                  flexDirection='column'
                  gap={2}
                  pl={4}
                  borderLeft='2px solid'
                  borderColor='gray.200'
                >
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_hyperlink_target`)}</Text>
                  <SelectField {...register('hyperlink_target')} isDisabled={!hasTicketLink}>
                    <option value={HyperlinkTarget.EXTERNAL}>
                      {t(`${I18N_PREFIX}.hyperlink_target_external`)}
                    </option>
                    <option value={HyperlinkTarget.INTERNAL}>
                      {t(`${I18N_PREFIX}.hyperlink_target_internal`)}
                    </option>
                    <option value={HyperlinkTarget.PARTNER}>
                      {t(`${I18N_PREFIX}.hyperlink_target_partner`)}
                    </option>
                  </SelectField>
                  <Text fontSize='sm' color='gray.500'>
                    {hasTicketLink
                      ? t(`${I18N_PREFIX}.hyperlink_target_helper`)
                      : t(`${I18N_PREFIX}.hyperlink_target_helper_empty`)}
                  </Text>
                </InputGroup>
              </Flex>
            </Box>

            <InputGroup display='flex' flexDirection='column'>
              <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_description`)}</Text>
              <Textarea rows={3} {...register('description')} />
            </InputGroup>

            <Controller
              name='is_show_information'
              control={control}
              render={({ field }) => (
                <Checkbox isChecked={field.value} onChange={field.onChange}>
                  {t(`${I18N_PREFIX}.field_is_show_information`)}
                </Checkbox>
              )}
            />

            <Controller
              name='is_last_matches'
              control={control}
              render={({ field }) => (
                <FormControl display='flex' gap={2} alignItems='center'>
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.last_matches_label`)}</Text>
                  <Switch
                    isChecked={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
              )}
            />

            {isLastMatches ? (
              <Flex direction='column' gap={2}>
                {lastMatchesFields.map((field, index) => (
                  <Flex
                    key={field.id}
                    direction={{ base: 'column', sm: 'row' }}
                    gap={4}
                    alignItems={{ sm: 'flex-start' }}
                    position='relative'
                  >
                    <ControlledSelect
                      variant='outline'
                      control={control}
                      width='180px'
                      name={`last_matches.${index}.home_team_uuid`}
                      options={matchTeamOptions}
                      onSelect={value => {
                        const opposite = oppositeUuid(value);
                        if (opposite) {
                          setValue(`last_matches.${index}.away_team_uuid`, opposite, {
                            shouldDirty: true,
                          });
                        }
                      }}
                    />

                    <Flex alignItems='center' gap={1}>
                      <NumericFormInput
                        control={control}
                        name={`last_matches.${index}.home_goals`}
                        width='50px'
                      />
                      <ChakraFeatherIcon name='x' width='18px' />
                      <NumericFormInput
                        control={control}
                        name={`last_matches.${index}.away_goals`}
                        width='50px'
                      />
                    </Flex>

                    <ControlledSelect
                      variant='outline'
                      control={control}
                      width='180px'
                      name={`last_matches.${index}.away_team_uuid`}
                      options={matchTeamOptions}
                      onSelect={value => {
                        const opposite = oppositeUuid(value);
                        if (opposite) {
                          setValue(`last_matches.${index}.home_team_uuid`, opposite, {
                            shouldDirty: true,
                          });
                        }
                      }}
                    />

                    <IconButton
                      aria-label={t(`${I18N_PREFIX}.remove_last_match_aria`)}
                      icon={<ChakraFeatherIcon name='trash-2' boxSize='14px' />}
                      variant='ghost'
                      color='red.500'
                      size='xs'
                      alignSelf='center'
                      onClick={() => removeLastMatch(index)}
                    />
                  </Flex>
                ))}

                <Button
                  variant='link'
                  color='brand.500'
                  textDecor='underline'
                  alignSelf='flex-start'
                  leftIcon={<ChakraFeatherIcon name='plus' />}
                  onClick={() =>
                    appendLastMatch({
                      home_team_uuid: item.home_team_uuid,
                      home_team_name: item.home_team?.name ?? '',
                      home_goals: '0',
                      away_team_uuid: item.away_team_uuid,
                      away_team_name: item.away_team?.name ?? '',
                      away_goals: '0',
                    })
                  }
                >
                  {t(`${I18N_PREFIX}.add_last_match_button`)}
                </Button>
              </Flex>
            ) : null}

            <Controller
              name='is_history_matches'
              control={control}
              render={({ field }) => (
                <FormControl display='flex' gap={2} alignItems='center'>
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.history_matches_label`)}</Text>
                  <Switch isChecked={field.value} onChange={field.onChange} />
                </FormControl>
              )}
            />

            {isHistoryMatches ? (
              <Flex gap={6}>
                <FormControl display='flex' gap={2} alignItems='center' width='fit-content'>
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.wins_label`)}</Text>
                  <NumericFormInput control={control} name='history_matches.wins' width='60px' />
                </FormControl>
                <FormControl display='flex' gap={2} alignItems='center' width='fit-content'>
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.draw_label`)}</Text>
                  <NumericFormInput control={control} name='history_matches.draw' width='60px' />
                </FormControl>
                <FormControl display='flex' gap={2} alignItems='center' width='fit-content'>
                  <Text fontWeight='medium'>{t(`${I18N_PREFIX}.lose_label`)}</Text>
                  <NumericFormInput control={control} name='history_matches.lose' width='60px' />
                </FormControl>
              </Flex>
            ) : null}
          </Flex>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant='outline' onClick={onClose} isDisabled={putMatch.isPending}>
            {t(`${I18N_PREFIX}.cancel_button`)}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} isLoading={putMatch.isPending}>
            {t(`${I18N_PREFIX}.save_button`)}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
